// src/features/privateAlbum/components/AlbumForm.jsx

import React, { useState, useEffect } from "react";
import { EVENT_TYPES } from "../types/privateAlbumTypes";
import { useSelector } from "react-redux";
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Chip,
  Stack,
  Typography,
  Grid,
  Divider,
  Alert,
  CircularProgress,
} from "@mui/material";

const AlbumForm = ({ initialData, onSubmit, onCancel, loading }) => {
  const { user } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    eventType: "wedding",
    eventDate: "",
    location: "",
    clientNames: "",
    clientEmail: "",
    createdBy: user?.email || "admin@example.com",
    ownerEmail: user?.email || "admin@example.com",
    tags: [],
    settings: {
      allowDownload: true,
      allowComments: true,
      watermark: false,
      passwordProtected: false,
      password: "",
    },
  });

  const [errors, setErrors] = useState({});
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || "",
        description: initialData.description || "",
        eventType: initialData.eventType || "wedding",
        eventDate: initialData.eventDate || "",
        location: initialData.location || "",
        clientNames: initialData.clientNames || "",
        clientEmail: initialData.clientEmail || "",
        createdBy: initialData.createdBy || user?.email || "admin@example.com",
        ownerEmail:
          initialData.ownerEmail || user?.email || "admin@example.com",
        tags: initialData.tags || [],
        settings: {
          allowDownload: initialData.settings?.allowDownload ?? true,
          allowComments: initialData.settings?.allowComments ?? true,
          watermark: initialData.settings?.watermark ?? false,
          passwordProtected: initialData.settings?.passwordProtected ?? false,
          password: initialData.settings?.password || "",
        },
      });
    } else {
      // Update createdBy when user changes
      setFormData((prev) => ({
        ...prev,
        createdBy: user?.email || "admin@example.com",
        ownerEmail: user?.email || "admin@example.com",
      }));
    }
  }, [initialData, user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith("settings.")) {
      const settingName = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        settings: {
          ...prev.settings,
          [settingName]: type === "checkbox" ? checked : value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate
    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }
    if (!formData.eventType) {
      newErrors.eventType = "Event type is required";
    }
    if (!formData.eventDate) {
      newErrors.eventDate = "Event date is required";
    }
    if (!formData.clientEmail.trim()) {
      newErrors.clientEmail = "Client email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.clientEmail)) {
      newErrors.clientEmail = "Invalid email format";
    }
    if (!formData.clientNames.trim()) {
      newErrors.clientNames = "Client name is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Add createdBy if not present
    const submitData = {
      ...formData,
      createdBy: formData.createdBy || user?.email || "admin@example.com",
      ownerEmail: formData.ownerEmail || user?.email || "admin@example.com",
    };

    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Title */}
        <TextField
          label="Album Title *"
          name="title"
          value={formData.title}
          onChange={handleChange}
          error={!!errors.title}
          helperText={errors.title}
          fullWidth
          required
          placeholder="e.g., Sarah & John Wedding"
          disabled={loading}
        />

        {/* Description */}
        <TextField
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          multiline
          rows={3}
          fullWidth
          placeholder="Describe the event..."
          disabled={loading}
        />

        {/* Event Type and Date */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.eventType}>
              <InputLabel>Event Type *</InputLabel>
              <Select
                name="eventType"
                value={formData.eventType}
                onChange={handleChange}
                label="Event Type *"
                disabled={loading}
              >
                {EVENT_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.eventType && (
                <Typography variant="caption" color="error">
                  {errors.eventType}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Event Date *"
              name="eventDate"
              type="date"
              value={formData.eventDate}
              onChange={handleChange}
              error={!!errors.eventDate}
              helperText={errors.eventDate}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              disabled={loading}
            />
          </Grid>
        </Grid>

        {/* Location */}
        <TextField
          label="Location"
          name="location"
          value={formData.location}
          onChange={handleChange}
          fullWidth
          placeholder="e.g., Beach Resort, Maldives"
          disabled={loading}
        />

        {/* Client Name and Email */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Client Name *"
              name="clientNames"
              value={formData.clientNames}
              onChange={handleChange}
              error={!!errors.clientNames}
              helperText={errors.clientNames}
              fullWidth
              required
              placeholder="e.g., Sarah Johnson"
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Client Email *"
              name="clientEmail"
              type="email"
              value={formData.clientEmail}
              onChange={handleChange}
              error={!!errors.clientEmail}
              helperText={errors.clientEmail}
              fullWidth
              required
              placeholder="client@email.com"
              disabled={loading}
            />
          </Grid>
        </Grid>

        {/* Tags */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Tags
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleAddTag(e)}
              placeholder="Add tags..."
              size="small"
              fullWidth
              disabled={loading}
            />
            <Button
              variant="outlined"
              onClick={handleAddTag}
              disabled={loading}
            >
              Add
            </Button>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
            {formData.tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                onDelete={() => handleRemoveTag(tag)}
                size="small"
                color="primary"
                variant="outlined"
                disabled={loading}
              />
            ))}
          </Box>
        </Box>

        <Divider />

        {/* Album Settings */}
        <Box>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            Album Settings
          </Typography>
          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Switch
                  name="settings.allowDownload"
                  checked={formData.settings.allowDownload}
                  onChange={handleChange}
                  disabled={loading}
                />
              }
              label="Allow Download"
            />
            <FormControlLabel
              control={
                <Switch
                  name="settings.allowComments"
                  checked={formData.settings.allowComments}
                  onChange={handleChange}
                  disabled={loading}
                />
              }
              label="Allow Comments"
            />
            <FormControlLabel
              control={
                <Switch
                  name="settings.watermark"
                  checked={formData.settings.watermark}
                  onChange={handleChange}
                  disabled={loading}
                />
              }
              label="Apply Watermark"
            />
            <FormControlLabel
              control={
                <Switch
                  name="settings.passwordProtected"
                  checked={formData.settings.passwordProtected}
                  onChange={handleChange}
                  disabled={loading}
                />
              }
              label="Password Protect"
            />
            {formData.settings.passwordProtected && (
              <TextField
                label="Password"
                name="settings.password"
                type="password"
                value={formData.settings.password}
                onChange={handleChange}
                size="small"
                placeholder="Set a password"
                disabled={loading}
                sx={{ mt: 1, maxWidth: 300 }}
              />
            )}
          </Stack>
        </Box>

        {errors.submit && <Alert severity="error">{errors.submit}</Alert>}

        {/* Actions */}
        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading}
            sx={{ py: 1.5 }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : initialData ? (
              "Update Album"
            ) : (
              "Create Album"
            )}
          </Button>
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            onClick={onCancel}
            disabled={loading}
            sx={{ py: 1.5, minWidth: 100 }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default AlbumForm;
